import type { PlainDsdNoteReferenceStatus } from "@/types/plain-dsd-table";

const HARD_RULE_LABELS: Record<string, string> = {
  cash_note_total: "현금및현금성자산 · 주석 세부내역 합계",
  cash_financial_instrument_category:
    "현금및현금성자산 · 범주별 금융상품(상각후원가)",
  cash_capital_risk: "현금및현금성자산 · 자본위험관리 차감액",
  ppe_depreciation: "감가상각비 · 유형자산 주석 합계",
  intangible_amortization: "무형자산상각비 · 무형자산 주석 합계",
  inventory_note_total: "재고자산 · 주석 장부금액 합계",
};

export const DSD_REVIEW_COPY = {
  header: {
    fallbackTitle: "DSD 문서 내용을 확인하세요",
    /** 상단 토글(베타) — 켜면 하드룰 연결 셀에 빗금 */
    hardRuleHighlight: "하드룰 연결",
  },
  /** 검토 내용 가져오기(베타 PoC) — 상단 버튼과 가져올 DSD 선택 모달 */
  reviewImport: {
    title: "검토 내용 가져오기",
    subtitle: "같은 회사·연도·기간·연결/별도의 DSD에서 가져올 수 있습니다.",
    currentDocumentLabel: "이 문서",
    reviewCount: (count: number) =>
      count > 0 ? `검토 ${count}건` : "저장된 검토 없음",
    picker: {
      title: "가져올 DSD",
      description: "같은 회사의 DSD",
      searchPlaceholder: "파일명·기간으로 검색",
      emptyMessage: "가져올 수 있는 다른 DSD가 없습니다.",
      errorMessage: "DSD 목록을 불러오는 중 오류가 발생했습니다.",
    },
    sourceReviews: {
      noSelection: "가져올 DSD를 선택해 주세요.",
      loading: "검토 건수를 확인하는 중...",
      error: "검토 건수를 불러오지 못했습니다.",
      retry: "다시 시도",
      empty: "이 DSD에는 저장된 검토가 없습니다.",
      /** 값이 달라진 항목은 넘어오지 않아 실제로 가져오는 수는 이보다 적을 수 있다 */
      ready: (count: number) =>
        `검토 ${count}건 중 값이 그대로인 항목을 가져옵니다.`,
    },
    cancel: "취소",
    submit: "가져오기",
    pending: "가져오는 중...",
    success:
      "검토 내용을 가져왔습니다. 값이 달라졌거나 찾지 못한 항목은 미검토로 남습니다.",
    sourceRejected:
      "같은 회사·연도·기간·연결/별도의 DSD에서만 가져올 수 있습니다.",
    failed: "검토 내용을 가져오지 못했습니다. 다시 시도해 주세요.",
  },
  rail: {
    ariaLabel: "검토 모드",
    "note-reference-review": "본문·주석매칭",
    "note-note-matching": "주석·주석매칭",
    "sum-validation": "합계 검증",
    corrections: "맞춤법 교정",
    "company-name-review": "기업명 확인",
    noData: "이 문서에는 해당 분석 결과가 없습니다",
    correctionPending: "오탈자 체크 진행 중입니다",
  },
  matching: {
    title: "본문·주석매칭",
    description:
      "본문의 금액이 주석과 일치하는지, 주기가 올바르게 기재됐는지 확인합니다.",
    noSelectionCaption: "선택된 셀이 없습니다.",
    noSelection: "표에서 셀을 선택해주세요",
    // 서버 응답이 "V2 분석 안 됨"과 "매칭 없음"을 구분하지 않아 문구도 구분하지 않는다
    noChecks: "이 문서에는 본문·주석 매칭 결과가 없습니다.",
  },
  /** 주석·주석매칭 — 검토(판단 저장) 없이 후보만 보여준다 */
  noteMatching: {
    noMatches: "이 문서에는 주석·주석 매칭 결과가 없습니다.",
    /** 후보 카드 칩 — 선택 셀과 같은 주석 안인지 */
    tag: { same: "같은 주석", other: "다른 주석" },
  },
  evidence: {
    title: "매칭된 주석",
    close: "닫기",
    prev: "이전 후보",
    next: "다음 후보",
    /** 머리글의 검토 버튼 — 패널 카드의 원형과 같은 모달을 연다. 검토 쌍이 있는 후보에만 */
    review: "검토 완료 처리",
    /**
     * 판단이 저장된 후보 — 같은 모달을 저장된 판단으로 채워 연다. 근거 창 버튼 문구이자 패널 사유 상자의 동작 이름
     * (저장은 같은 POST, 백엔드가 기존 기록을 무효화하고 새로 쌓는다)
     */
    editReview: "검토 사유 수정",
    /** 값도 계정명도 못 맞춘 후보(청크 쌍) — 보여줄 근거 표가 없다 */
    noEvidenceTitle: "판단 근거를 찾지 못했습니다",
    noEvidenceBody:
      "이 항목은 판정 과정을 설명할 자료가 없습니다.\n주석 원문과 직접 비교해 검토해 주세요.",
  },
  panel: {
    /** 다른 DSD에서 가져온 판단 표시 (임시). 출처 파일명은 수정 모달에서 보여 준다 */
    inherited: "이전 버전에서 판단 가져옴",
    /**
     * 값도 계정명도 못 맞춘 청크 쌍 — 선택 셀의 상태 배너(금액 일치 전제)가 안 맞아 따로 쓴다.
     * 패널 배너(unconfirmed 셀에 참조 주석의 빈 쌍이 있을 때)와 검토 모달의 카드 배너가 같은 문장을 쓴다
     */
    noEvidenceBanner: {
      referenced: "참조된 주석에서 일치하는 금액과 계정명을 찾지 못했습니다.",
      unreferenced:
        "주석 번호 표기가 없고 금액 · 계정명도 확인하지 못했습니다.",
    },
    requiredItems: "필수 확인 항목",
    additionalItems: "추가 확인 항목",
    /** 카드 태그 — 본문 주석번호와의 관계 */
    tag: {
      referenced: "참조 주석",
      other: "다른 주석",
      /** 본문에 주석번호가 없으면 매칭된 주석도 "다른 주석"으로 부른다 (Figma 7730-589281) */
      unreferenced: "다른 주석",
      /** 본문(재무제표) 쪽 후보 */
      body: "본문",
    },
    evidence: "근거 확인",
    review: "검토하기",
    /** 검토 원형에 커서를 올렸을 때 */
    reviewTooltip: "검토를 마쳤거나 문제가 없는 항목이라면 체크해주세요.",
    pendingOnly: "검토 필요 항목만 보기",
    /** 필터를 켠 채 마지막 후보까지 검토했을 때 */
    allReviewed: "모든 항목을 검토했어요",
    /** 색이 없는 셀(주석 연결 없음)을 골랐을 때 — 상태 배너 대신 */
    noLinkedNote: "연결된 주석이 없습니다.",
    reviewHint:
      "검토를 마쳤거나 문제가 없는 항목은 검토 완료로 체크해 두세요.\n남은 항목만 보며 검토를 이어갈 수 있습니다.",
    /** 검토 완료 배지 — 판단별 */
    reviewedBadge: {
      no_issue: "검토 완료",
      needs_follow_up: "추가 확인 필요",
      unrelated: "검토 완료",
    },
    scrollToCell: "표에서 보기",
    emptyValue: "(빈 값)",
    /** 하드룰이 만든 연결의 카드(베타). 뒤에 규칙 표기(hardRuleLabel)가 붙는다 */
    hardRule: "하드룰",
    /** 선택 셀 요약(베타) — 이 본문 셀에 걸린 서로 다른 하드룰 수. 뒤에 규칙 표기 목록이 붙는다 */
    hardRuleCount: (count: number) => `하드룰 ${count}개 연결`,
    /**
     * 백엔드 하드룰 id → 한글 표기(본문 계정 · 대조하는 주석 값). 응답 타입이 string 이라 enum 이 아니다 —
     * 여기 없는 id 는 규칙이 늘어난 것이므로 id 그대로 보이고, 표기는 이 표에 한 줄 보태면 된다
     */
    hardRuleLabel: (rule: string) => HARD_RULE_LABELS[rule] ?? rule,
    /** 선택 셀 상태 배너 (Figma 7647-490853 패널 5종). 여기 없는 상태는 statusLabel */
    banner: {
      matched: "본문의 항목이 참조된 주석과 일치합니다.",
      valueOnlyUncertain:
        "금액이 일치하는 주석이 있지만, 계정명이 달라 같은 항목인지 확실하지 않습니다.",
      referenceMissing:
        "금액 · 계정명이 일치하지만, 본문에 주석번호 표기가 없습니다.",
      unreferencedPartial:
        "금액 혹은 계정명이 다르고, 본문에 주석번호 표기가 없습니다.",
    },
    /** 후보 카드 칩. 참조 주석은 일치 항목을, 다른 주석·참조 주석 없음은 불일치 항목을 보여준다 */
    matchLabel: {
      both: "금액 · 계정명 일치",
      value: "금액 일치",
      text: "계정명 일치",
      noAmount: "금액 없음",
      amountMismatch: "금액 불일치",
      textMismatch: "계정명 불일치",
      bothMismatch: "금액 · 계정명 불일치",
    },
  },
  /** 검토 모달 (Figma "Floating Panel / popover") */
  reviewModal: {
    title: "검토 완료로 처리할까요?",
    /** 가져온 판단을 수정할 때 출처 (임시 표시) */
    inheritedFrom: (filename: string) =>
      `이전 버전에서 판단 가져옴 · ${filename}`,
    subtitle: "이 계정과 관련된 금액인지 확인해 주세요.",
    /** 저장된 판단을 바꾸는 경우 — 제목·부제는 처음 검토와 같고 버튼만 다르다 */
    editSubmit: "수정",
    choices: {
      needs_follow_up: "추가 확인 필요",
      no_issue: "문제 없는 항목",
    },
    reasonLabel: "검토 사유를 입력해주세요",
    /** placeholder — 카드 배너 케이스(톤) × 판단. 빨강(금액·계정명 일치, 주석번호 없음) / 노랑(금액 혹은 계정명 다름, 주석번호 없음) */
    reasonPlaceholder: {
      negative: {
        needs_follow_up: "예) 주석은 천원 단위로 표기되어 있습니다.",
        no_issue: "예) 주석번호를 표기하지 않는 것이 회사 관행입니다.",
      },
      caution: {
        needs_follow_up: "예) 주석번호 표기에 추가 검토가 필요합니다.",
        no_issue: "예) 우연히 숫자가 일치하는 항목입니다.",
      },
      positive: {
        needs_follow_up: "예) 주석번호 표기에 추가 검토가 필요합니다.",
        no_issue: "예) 우연히 숫자가 일치하는 항목입니다.",
      },
    },
    cancel: "취소",
    submit: "검토 완료",
  },
  guide: {
    howTo: "이렇게 사용해보세요",
    "note-reference-review": {
      description:
        "재무제표 본문의 금액이 주석과 일치하는지, 주기가 올바르게 기재되었는지 확인합니다.",
      steps: [
        {
          title: "1. 검토할 셀 선택",
          description:
            "표에서 셀을 클릭하면 해당 항목의\n검토 결과를 확인할 수 있습니다.",
        },
        {
          title: "2. 참조 위치 확인",
          description:
            "선택한 셀과 같은 값이 본문과 주석의 어디에 기재되었는지 확인할 수 있습니다.",
        },
        {
          title: "3. 불일치 항목 확인",
          description:
            "본문과 주석의 금액 혹은 계정명이 다른 셀은 경고 표시되어 바로 확인할 수 있습니다.",
        },
      ],
    },
    "note-note-matching": {
      description:
        "주석의 금액이 다른 주석(또는 같은 주석의 다른 표)과 일치하는지 확인합니다.",
      steps: [
        {
          title: "1. 검토할 셀 선택",
          description:
            "주석 표에서 색이 칠해진 셀을 클릭하면\n같은 금액이 기재된 위치를 확인할 수 있습니다.",
        },
        {
          title: "2. 참조 위치 확인",
          description:
            "선택한 셀과 같은 값이 주석의 어디에 기재되었는지 확인할 수 있습니다.",
        },
        {
          title: "3. 근거 확인",
          description:
            "후보를 누르면 그 주석 표를 선택한 셀 아래 근거 창으로 볼 수 있습니다.",
        },
      ],
    },
    "sum-validation": {
      description: "재무제표 표 안의 합계가 올바르게 계산되었는지 확인합니다.",
      steps: [
        {
          title: "1. 합계 셀 확인",
          description:
            "표에서 합계 셀은 주황색으로 표시되어 한눈에 확인할 수 있습니다.",
        },
        {
          title: "2. 합계 셀 선택",
          description:
            "합계 셀을 클릭하면 어떤 값들의 합인지 확인할 수 있습니다.",
        },
        {
          title: "3. 합계 오류 확인",
          description:
            "합계가 맞지 않는 셀은 검정색으로 표시되어 오류를 바로 확인할 수 있습니다.",
        },
      ],
    },
    corrections: {
      description: "재무제표의 문법적 오류 사항을 확인합니다.",
      steps: [
        {
          title: "1. 문법 위배사항 확인",
          description: "오탈자와 띄어쓰기 오류를 확인할 수 있습니다.",
        },
        {
          title: "2. 오류 종류 필터링",
          description:
            "오탈자와 띄어쓰기 오류를 각각 필터링해서 확인할 수 있습니다.",
        },
        {
          title: "3. 오류 사항 제외 체크",
          description:
            "재무제표 작성 관행 혹은 의도에 맞게 작성된 것은 체크를 통해 오류 사항에서 제외할 수 있습니다.",
        },
      ],
    },
    "company-name-review": {
      description:
        "재무제표에서 기업명이 정확하고 일관되게 사용되었는지를 확인합니다.",
      steps: [
        {
          title: "1. 기업명 확인",
          description: "재무제표에 등록된 기업명을 모두 확인할 수 있습니다.",
        },
        {
          title: "2. 확인 필요한 기업명 클릭",
          description:
            "여러 번 등장한 기업명의 표기가 일관되지 않은 경우, 경고 표시가 나타나 확인이 필요한 기업명을 바로 확인할 수 있습니다.",
        },
        {
          title: "3. 기업명 상세 확인",
          description:
            "경고 표시가 뜬 기업명의 표기 차이를 자세히 확인할 수 있습니다. (쉼표 누락 등)",
        },
      ],
    },
  },
} as const;

/** 주석 매칭 상태 문구 — 셀 툴팁, 그리고 Figma 배너 문구가 없는 상태의 패널 배너 */
export const DSD_REVIEW_STATUS_LABELS: Readonly<
  Record<PlainDsdNoteReferenceStatus, string>
> = {
  confirmed: "주석 연결이 확인되었습니다.",
  unconfirmed: "주석 연결 근거가 부족합니다.",
  reference_missing: "텍스트·값은 맞지만 주석 번호가 주기에 없습니다.",
  unlinked: "주석 연결이 없어 주석 대조가 제한됩니다.",
};
